import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import useToastPromise from "../../../hooks/useToast";
import { schema } from "../../../schemas/createBoard";
import { createBoard } from "../../../services/apiBoards";
import { useWorkflow } from "../../../reactQuery/workflows";
import { useUsers } from "../../../reactQuery/users";
import {
  CREATE_BOARD_ACCESS_OPTIONS,
  getActiveUserOptions,
  getWorkflowOptions,
  normalizeCreateBoardPayload,
} from "../utils/dashboardUtils";

export function useCreateBoardForm({ onClose }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToastPromise } = useToastPromise();
  const {
    isLoading: isWorkflowLoading,
    workflows,
    isError: isWorkflowError,
  } = useWorkflow();
  const {
    isLoading: isUserLoading,
    users,
    isError: isUserError,
  } = useUsers();

  const methods = useForm({
    defaultValues: {
      is_archive: false,
    },
    resolver: yupResolver(schema),
  });
  const { reset } = methods;

  const workflowOptions = React.useMemo(
    () => getWorkflowOptions(workflows),
    [workflows]
  );
  const userOptions = React.useMemo(() => getActiveUserOptions(users), [users]);

  const { mutate: createBoardMutate, isPending: loading } = useMutation({
    mutationFn: async (data) => showToastPromise(createBoard, data),
    onSuccess: (res) => {
      const boardKey =
        res?.data?.data?.responseData?.key ??
        res?.data?.data?.responseData?.board_key;

      queryClient.invalidateQueries({
        queryKey: ["boards"],
      });
      reset();
      onClose();

      if (boardKey) {
        navigate(`/board/${boardKey}`);
      }
    },
    onError: (error) => {
      console.error("Error Create Boards", error);
    },
  });

  const submit = React.useCallback(
    (data) => {
      createBoardMutate(normalizeCreateBoardPayload(data));
    },
    [createBoardMutate]
  );

  return {
    methods,
    loading,
    isWorkflowLoading,
    isWorkflowError,
    isUserLoading,
    isUserError,
    workflowOptions,
    userOptions,
    accessOptions: CREATE_BOARD_ACCESS_OPTIONS,
    submit,
  };
}
